import React from 'react';
import { useForm } from 'react-hook-form';
import { PaymentButton } from './PaymentButton';
import { useCheckoutStore } from '../../store/checkoutStore';
import { useProductStore } from '../../store/productStore';
import { Check, ArrowLeft } from 'lucide-react';
import { BumpOffer } from './BumpOffer';

interface Props {
  settings: {
    heading: string;
    features: string[];
    buttonText: string;
    primaryColor: string;
    logo?: string;
    showCouponOption?: boolean;
  };
}

interface FormData {
  email: string;
  name: string;
  phone: string;
}

export const ModernSplitTemplate = ({ settings }: Props) => {
  const { currentPage, updatePageSettings } = useCheckoutStore();
  const getProduct = useProductStore((state) => state.getProduct);
  const { register, watch } = useForm<FormData>();
  
  const mainProduct = currentPage?.productId ? getProduct(currentPage.productId) : null;
  const bumpProduct = currentPage?.bumpOffer?.productId ? getProduct(currentPage.bumpOffer.productId) : null;
  const [showPromoCode, setShowPromoCode] = React.useState(false);

  const formData = watch();

  const handleBumpChange = (enabled: boolean) => {
    if (currentPage?.bumpOffer) {
      updatePageSettings({
        bumpOffer: { ...currentPage.bumpOffer, enabled }
      });
    }
  };

  if (!mainProduct || !currentPage) return null;

  const platformFee = 4;
  const bumpAmount = currentPage.bumpOffer?.enabled && bumpProduct ? bumpProduct.price : 0;
  const totalAmount = mainProduct.price + platformFee + bumpAmount;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-8">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="grid grid-cols-2">
            {/* Left Panel - Payment Form */}
            <div className="p-8">
              <div className="flex items-center gap-2 mb-8">
                <button 
                  onClick={() => window.history.back()}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h2 className="text-2xl font-semibold">Payment details</h2>
              </div>
              
              <form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    {...register('name', { required: true })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email address
                  </label>
                  <input
                    type="email"
                    {...register('email', { required: true })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    {...register('phone', { required: true })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors"
                    placeholder="+91 98765 43210"
                  />
                </div>

                {/* Bump Offer */}
                {currentPage.bumpOffer && (
                  <BumpOffer onBumpChange={handleBumpChange} />
                )}

                {settings.showCouponOption && (
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showPromoCode}
                        onChange={(e) => setShowPromoCode(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      I've a promo code
                    </label>
                  </div>
                )}

                {showPromoCode && (
                  <div>
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors"
                      placeholder="Enter promo code"
                    />
                  </div>
                )}

                <div className="space-y-2 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">₹{mainProduct.price}</span>
                  </div>
                  {bumpAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Order Bump</span>
                      <span className="font-medium">₹{bumpAmount}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Platform Fee</span>
                    <span className="font-medium">₹{platformFee}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                    <span>Total Amount</span>
                    <span>₹{totalAmount}</span>
                  </div>
                </div>

                <PaymentButton 
                  amount={totalAmount}
                  email={formData.email}
                  name={formData.name}
                  productName={mainProduct.name}
                />
              </form>
            </div>

            {/* Right Panel - Product Details */}
            <div className="relative">
              <div 
                className="absolute inset-0 bg-gradient-to-br"
                style={{ 
                  backgroundImage: `linear-gradient(to bottom right, ${settings.primaryColor}, ${settings.primaryColor}dd)`
                }}
              ></div>
              <div className="relative z-10 p-8 text-white">
                {settings.logo && (
                  <img 
                    src={settings.logo} 
                    alt="Logo" 
                    className="h-8 mb-8"
                  />
                )}

                <div className="flex items-center gap-2 mb-8">
                  <div className="w-8 h-8 rounded-full bg-blue-500"></div>
                  <div className="w-8 h-8 rounded-full bg-white"></div>
                </div>

                <h1 className="text-3xl font-bold mb-8">
                  {settings.heading}
                </h1>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-coral-500 rounded-xl"></div>
                    <div>
                      <h3 className="font-medium">{mainProduct.name}</h3>
                      <p className="text-2xl font-bold">₹{mainProduct.price}</p>
                    </div>
                  </div>
                </div>

                <ul className="space-y-4">
                  {settings.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};